import styled, { css, keyframes } from "styled-components";
import { FLEX } from "styles/flex";
import { calcRem, isLight } from "styles/theme";
import { COLORS } from "styles/colors";
import * as mediaQuery from "components/MediaQuery";

type ConfirmStatus = "loading" | "success" | "error" | "invalid";

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const glow = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.45;
  }
  50% {
    transform: scale(1.08);
    opacity: 0.7;
  }
  100% {
    transform: scale(1);
    opacity: 0.45;
  }
`;

export const Page = styled.main`
  ${FLEX.FlexCenterCenter};
  position: relative;
  min-height: 100vh;
  padding: 24px;
  overflow: hidden;
  background: ${({ theme }) =>
    isLight(theme)
      ? `linear-gradient(135deg, ${COLORS.WHITE} 0%, ${COLORS.WHITE} 50%, ${COLORS.WHITE} 100%)`
      : `linear-gradient(145deg, ${COLORS.PRIMARY2} 0%, ${COLORS.PRIMARY1} 45%, ${COLORS.PRIMARY3} 100%)`};
`;

export const BackgroundDecor = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;

  &::before,
  &::after {
    content: "";
    position: absolute;
    border-radius: 999px;
    filter: blur(40px);
    animation: ${glow} 6s ease-in-out infinite;
  }

  &::before {
    top: -120px;
    right: -120px;
    width: 300px;
    height: 300px;
    background: ${`${COLORS.SELECTED}40`};
  }

  &::after {
    bottom: -130px;
    left: -120px;
    width: 340px;
    height: 340px;
    background: ${`${COLORS.COMPANY}3D`};
    animation-delay: 1.2s;
  }
`;

export const Card = styled.section`
  position: relative;
  z-index: 1;
  width: min(100%, 620px);
  border-radius: 20px;
  padding: 34px 30px;
  border: 1px solid ${({ theme }) => `${theme.COLORS.WHITE}20`};
  background: ${({ theme }) =>
    isLight(theme)
      ? `${COLORS.WHITE}D1`
      : `linear-gradient(170deg, ${COLORS.PRIMARY1}D9, ${COLORS.PRIMARY2}EB)`};
  box-shadow: 0 24px 64px ${`${COLORS.BLACK}42`};
  backdrop-filter: blur(8px);
  animation: ${fadeUp} 0.35s ease-out;

  ${mediaQuery.isMobile} {
    border-radius: 16px;
    padding: 24px 20px;
  }
`;

export const Header = styled.header`
  ${FLEX.FlexStartStart};
  flex-direction: column;
  gap: 8px;
`;

export const Title = styled.h1`
  margin: 2px 0 0;
  font-size: ${calcRem(27)};
  line-height: 1.2;
  font-weight: 500;
  color: ${({ theme }) => theme.COLORS.WHITE};
`;

export const Subtitle = styled.p`
  margin: 0;
  font-size: ${calcRem(14)};
  line-height: 1.45;
  color: ${({ theme }) => `${theme.COLORS.WHITE}9A`};
`;

const panelByStatus = {
  loading: css`
    border-color: ${`${COLORS.WARNING}66`};
    background: ${`${COLORS.WARNING}17`};
  `,
  success: css`
    border-color: ${`${COLORS.GREEN}73`};
    background: ${`${COLORS.GREEN}1A`};
  `,
  error: css`
    border-color: ${`${COLORS.VALIDATERED}6B`};
    background: ${`${COLORS.VALIDATERED}1A`};
  `,
  invalid: css`
    border-color: ${`${COLORS.VALIDATERED}6B`};
    background: ${`${COLORS.VALIDATERED}1A`};
  `,
};

export const StatusPanel = styled.div<{ status: ConfirmStatus }>`
  ${FLEX.FlexStartStart};
  flex-direction: column;
  margin-top: 22px;
  border: 1px solid;
  border-radius: 14px;
  padding: 18px;
  ${({ status }) => panelByStatus[status]}
`;

export const StatusMainRow = styled.div`
  ${FLEX.FlexStartStart};
  width: 100%;
  gap: 12px;
`;

export const StatusTextGroup = styled.div`
  ${FLEX.FlexStartStart};
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  flex: 1;
`;

const iconByStatus = {
  loading: css`
    color: ${COLORS.WARNING};
    background: ${`${COLORS.WARNING}21`};
  `,
  success: css`
    color: ${COLORS.GREEN};
    background: ${`${COLORS.GREEN}24`};
  `,
  error: css`
    color: ${COLORS.VALIDATERED};
    background: ${`${COLORS.VALIDATERED}24`};
  `,
  invalid: css`
    color: ${COLORS.VALIDATERED};
    background: ${`${COLORS.VALIDATERED}24`};
  `,
};

export const StatusIcon = styled.div<{ status: ConfirmStatus }>`
  ${FLEX.FlexCenterCenter};
  width: 52px;
  height: 52px;
  border-radius: 14px;
  font-size: ${calcRem(26)};
  ${({ status }) => iconByStatus[status]}
`;

export const StatusTitle = styled.h2`
  margin: 0;
  font-size: ${calcRem(22)};
  line-height: 1.1;
  font-weight: 500;
  color: ${({ theme }) => theme.COLORS.WHITE};
  ${FLEX.FlexStartCenter};
  gap: 8px;
`;

export const Message = styled.p`
  margin: 0;
  font-size: ${calcRem(14)};
  line-height: 1.5;
  color: ${({ theme }) => `${theme.COLORS.WHITE}CF`};
  white-space: pre-wrap;
`;

export const LoaderRow = styled.div`
  ${FLEX.FlexStartCenter};
  min-height: 24px;
`;

export const MetaText = styled.div`
  font-size: ${calcRem(13)};
  color: ${({ theme }) => `${theme.COLORS.WHITE}B8`};
`;

export const ButtonRow = styled.div`
  ${FLEX.FlexEndCenter};
  gap: 10px;
  margin-top: 20px;

  > button {
    min-width: 150px;
  }

  ${mediaQuery.isMobile} {
    ${FLEX.FlexStartCenter};
    width: 100%;

    > button {
      width: 100%;
    }
  }
`;
